import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SuperCompanies() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [newStatus, setNewStatus] = useState("active");

  const load = async () => {
    const { data } = await api.get("/super/companies");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q);
  });

  const changeStatus = async () => {
    try {
      await api.post(`/super/companies/${selected.id}/status`, { status: newStatus, reason });
      toast.success("Updated");
      setSelected(null); setReason("");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="super-companies-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Tenants</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Companies</h1>
      </div>

      <div className="relative w-full sm:w-96">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input placeholder="Search companies…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-[#18181B] border-white/10 text-zinc-200" data-testid="super-companies-search" />
      </div>

      <div className="bg-[#18181B] border border-white/5 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5">
              <TableHead className="text-zinc-400">Name</TableHead>
              <TableHead className="text-zinc-400">Type</TableHead>
              <TableHead className="text-zinc-400">Email</TableHead>
              <TableHead className="text-zinc-400">Phone</TableHead>
              <TableHead className="text-zinc-400">Users</TableHead>
              <TableHead className="text-zinc-400">Bookings</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-10">No companies.</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} className="border-white/5 hover:bg-white/5" data-testid={`super-company-row-${c.id}`}>
                <TableCell className="text-zinc-200">{c.name}</TableCell>
                <TableCell className="text-zinc-400">{c.company_type_name || "—"}</TableCell>
                <TableCell className="text-zinc-400">{c.email}</TableCell>
                <TableCell className="text-zinc-400">{c.phone || "—"}</TableCell>
                <TableCell className="text-zinc-400">{c.last_login_at ? new Date(c.last_login_at).toLocaleString() : "—"}</TableCell>
                <TableCell className="text-zinc-300">{c.users_count}</TableCell>
                <TableCell className="text-zinc-300">{c.bookings_count}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    c.status === "active" ? "bg-emerald-900/40 text-emerald-300 border-emerald-800" :
                    c.status === "suspended" ? "bg-amber-900/40 text-amber-300 border-amber-800" :
                    "bg-zinc-800 text-zinc-300 border-zinc-700"
                  }>{c.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:bg-white/10" onClick={() => { setSelected(c); setNewStatus(c.status); }} data-testid={`super-company-manage-${c.id}`}>Manage</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#18181B] border-white/10 text-zinc-200">
          <DialogHeader><DialogTitle>{selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Email</div><div>{selected.email}</div></div>
              <div><div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Type</div><div>{selected.company_type_name || "—"}</div></div>
              <div><div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Status</div>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-[#0a0a0c] border-white/10 mt-1" data-testid="super-company-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="deleted">Deleted (soft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Reason (logged)</div><Textarea className="bg-[#0a0a0c] border-white/10 mt-1" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="super-company-reason" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
            <Button onClick={changeStatus} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="super-company-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
